<template>
  <a-drawer
    :title="$t('invoice.filter')"
    placement="right"
    :closable="false"
    :visible="visible"
    @close="$emit('cancel')"
    :zIndex="1020"
    width="400px"
  >
    <div class="row">
      <div class="col-md-12">
        <div class="form-group">
          <label>{{ $t('attendanceAbsenceNotification.branch') }}</label>
          <a-select
            style="width: 100%"
            :value="branchId"
            :options="branches"
            showSearch
            mode="multiple"
            :filterOption="filterOption"
            :maxTagCount="1"
            :maxTagTextLength="17"
            :placeholder="$t('all')"
            :loading="loading"
            @change="updateBranch"
          />
        </div>
        <div class="form-group">
          <label>{{ $t('attendanceAbsenceNotification.academicYear') }}</label>
          <a-select
            style="width: 100%"
            :value="filters.ac_year_id"
            :options="academicYearOptions"
            showSearch
            mode="multiple"
            :filterOption="filterOption"
            :maxTagCount="1"
            :maxTagTextLength="17"
            :placeholder="$t('all')"
            :loading="loading"
            :disabled="loading"
            @change="onAcademicYearChange"
          />
        </div>
        <div class="form-group">
          <label>{{ $t('attendanceAbsenceNotification.mainProgram') }}</label>
          <a-select
            style="width: 100%"
            :value="filters.acp_id"
            :options="mainProgramOptions"
            showSearch
            mode="multiple"
            :filterOption="filterOption"
            :maxTagCount="1"
            :maxTagTextLength="17"
            :placeholder="$t('all')"
            :loading="loading"
            :disabled="loading"
            @change="onMainProgramChange"
          />
        </div>
        <div class="form-group">
          <label>{{ $t('attendanceAbsenceNotification.subProgram') }}</label>
          <a-select
            style="width: 100%"
            :value="filters.acps_id"
            :options="subProgramOptions"
            showSearch
            mode="multiple"
            :filterOption="filterOption"
            :maxTagCount="1"
            :maxTagTextLength="17"
            :placeholder="$t('all')"
            :loading="loading"
            :disabled="loading"
            @change="onSubProgramChange"
          />
        </div>
        <div class="form-group">
          <label>{{ $t('attendanceAbsenceNotification.academicTerm') }}</label>
          <a-select
            style="width: 100%"
            :value="filters.ac_term_id"
            :options="academicTermOptions"
            showSearch
            mode="multiple"
            :filterOption="filterOption"
            :maxTagCount="1"
            :maxTagTextLength="17"
            :placeholder="$t('all')"
            :loading="loading"
            :disabled="loading"
            @change="updateFilter('ac_term_id', $event)"
          />
        </div>
        <div class="form-group">
          <label>{{ $t('attendanceAbsenceNotification.status') }}</label>
          <a-select
            style="width: 100%"
            :value="filters.is_sent"
            :options="statusOptions"
            :placeholder="$t('all')"
            allowClear
            :disabled="loading"
            @change="updateFilter('is_sent', $event)"
          />
        </div>
        <div class="d-flex justify-content-between mt-3">
          <ts-button
            outline
            :disabled="loading"
            v-on:click.prevent="$emit('clear')"
          >
            {{ $t('clear') }}
          </ts-button>
          <div>
            <ts-button
              outline
              :disabled="loading"
              v-on:click.prevent="$emit('cancel')"
            >
              {{ $t('cancel') }}
            </ts-button>
            <ts-button
              color="primary"
              class="ml-2"
              :disabled="loading"
              v-on:click.prevent="$emit('apply')"
            >
              {{ $t('view') }}
            </ts-button>
          </div>
        </div>
      </div>
    </div>
  </a-drawer>
</template>

<script>
export default {
  name: 'attendanceAbsenceNotificationFilterDrawer',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    branchId: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Object,
      required: true
    },
    branches: {
      type: Array,
      default: () => []
    },
    academicYears: {
      type: Array,
      default: () => []
    },
    mainPrograms: {
      type: Array,
      default: () => []
    },
    subPrograms: {
      type: Array,
      default: () => []
    },
    academicTerms: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    academicYearOptions() {
      return this.academicYears
        .filter(this.optionMatchesSelectedBranches)
        .map(el => ({ value: el.ac_year_id, label: el.option_label }))
    },
    mainProgramOptions() {
      const mainProgramIds = this.allowedMainProgramIds()

      return this.mainPrograms
        .filter(this.optionMatchesSelectedBranches)
        .filter(
          el => !mainProgramIds.length || this.idIn(mainProgramIds, el.acp_id)
        )
        .map(el => ({ value: el.acp_id, label: el.option_label }))
    },
    subProgramOptions() {
      const subProgramIds = this.allowedSubProgramIds()

      return this.subPrograms
        .filter(this.optionMatchesSelectedBranches)
        .filter(
          el =>
            !this.filters.acp_id.length ||
            this.idIn(this.filters.acp_id, el.acp_id)
        )
        .filter(
          el => !subProgramIds.length || this.idIn(subProgramIds, el.acps_id)
        )
        .map(el => ({ value: el.acps_id, label: el.option_label }))
    },
    academicTermOptions() {
      return this.academicTerms
        .filter(this.optionMatchesSelectedBranches)
        .filter(term => this.termMatchesFilters(term))
        .map(el => ({ value: el.ac_term_id, label: el.option_label }))
    },
    statusOptions() {
      return [
        {
          value: 'pending',
          label: this.$t('attendanceAbsenceNotification.pending')
        },
        { value: 'sent', label: this.$t('attendanceAbsenceNotification.sent') }
      ]
    }
  },
  methods: {
    idIn(values, value) {
      return values.map(item => String(item)).includes(String(value))
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    },
    selectedBranchIds() {
      return Array.isArray(this.branchId)
        ? this.branchId.map(branchId => String(branchId))
        : []
    },
    optionMatchesSelectedBranches(option) {
      const selectedBranchIds = this.selectedBranchIds()

      if (!selectedBranchIds.length || !option.branch_ids) {
        return true
      }

      return option.branch_ids.some(branchId =>
        selectedBranchIds.includes(String(branchId))
      )
    },
    allowedMainProgramIds() {
      if (!this.filters.ac_year_id.length) {
        return []
      }

      const subProgramIds = this.allowedSubProgramIds()

      return [
        ...new Set(
          this.subPrograms
            .filter(subProgram => this.idIn(subProgramIds, subProgram.acps_id))
            .map(subProgram => subProgram.acp_id)
        )
      ]
    },
    allowedSubProgramIds() {
      if (!this.filters.ac_year_id.length) {
        return []
      }

      return [
        ...new Set(
          this.academicTerms
            .filter(this.optionMatchesSelectedBranches)
            .filter(term => this.idIn(this.filters.ac_year_id, term.ac_year_id))
            .map(term => term.acps_id)
        )
      ]
    },
    termMatchesFilters(term) {
      const subProgram = this.subPrograms.find(el =>
        this.idIn([el.acps_id], term.acps_id)
      )

      if (
        this.filters.ac_year_id.length &&
        !this.idIn(this.filters.ac_year_id, term.ac_year_id)
      ) {
        return false
      }

      if (
        this.filters.acp_id.length &&
        (!subProgram || !this.idIn(this.filters.acp_id, subProgram.acp_id))
      ) {
        return false
      }

      if (
        this.filters.acps_id.length &&
        !this.idIn(this.filters.acps_id, term.acps_id)
      ) {
        return false
      }

      return true
    },
    updateBranch(value) {
      this.$emit('update:branch-id', value)
    },
    updateFilters(filters) {
      this.$emit('update:filters', filters)
    },
    updateFilter(key, value) {
      this.updateFilters({
        ...this.filters,
        [key]: value
      })
    },
    onAcademicYearChange(value) {
      this.updateFilters({
        ...this.filters,
        ac_year_id: value,
        acp_id: [],
        acps_id: [],
        ac_term_id: [],
        is_sent: this.filters.is_sent
      })
    },
    onMainProgramChange(value) {
      this.updateFilters({
        ...this.filters,
        acp_id: value,
        acps_id: [],
        ac_term_id: [],
        is_sent: this.filters.is_sent
      })
    },
    onSubProgramChange(value) {
      this.updateFilters({
        ...this.filters,
        acps_id: value,
        ac_term_id: [],
        is_sent: this.filters.is_sent
      })
    }
  }
}
</script>
